import { baseApi } from 'services/api';
import { API_VERSION } from 'constants/common';
import { DeviceFingerprintService } from './fingerprintService';
import type { DeviceFingerprintEventType, DeviceFingerprintResult } from './types';

const SESSION_STORAGE_KEY = 'device_fingerprint_session_id';

export type DeviceFingerprintRequestBody = {
  eventType: DeviceFingerprintEventType;
  teamId: number;
  merchantId: string;
  userAgent: string;
  platform: string;
  language: string;
  languages: string[];
  webdriver: boolean;
  screenWidth: number;
  screenHeight: number;
  screenColorDepth: number;
  devicePixelRatio: number;
  hardwareConcurrency: number;
  deviceMemory: number;
  maxTouchPoints: number;
  timezone: string;
  timezoneOffset: number;
  canvasHash: string;
  sessionId: string;
};

export type SendDeviceFingerprintParams = {
  eventType: DeviceFingerprintEventType;
  teamId?: number;
  merchantId?: string;
};

export const getOrCreateSessionId = (): string => {
  try {
    let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId = `sess_${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 15)}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }
};

export const buildDeviceFingerprintRequestBody = (
  fingerprint: DeviceFingerprintResult,
  params: SendDeviceFingerprintParams & { sessionId: string }
): DeviceFingerprintRequestBody => {
  const { signals } = fingerprint;
  const [screenWidth, screenHeight] = signals.screenResolution;
  return {
    eventType: params.eventType,
    teamId: params.teamId ?? 0,
    merchantId: params.merchantId ?? '',
    userAgent: signals.userAgent,
    platform: signals.platform,
    language: signals.language,
    languages: signals.languages,
    webdriver: signals.webdriver,
    screenWidth,
    screenHeight,
    screenColorDepth: signals.screenColorDepth,
    devicePixelRatio: signals.devicePixelRatio,
    hardwareConcurrency: signals.hardwareConcurrency,
    deviceMemory: signals.deviceMemory ?? 0,
    maxTouchPoints: signals.maxTouchPoints,
    timezone: signals.timezone,
    timezoneOffset: signals.timezoneOffset,
    canvasHash: signals.canvasHash ?? '',
    sessionId: params.sessionId
  };
};

/**
 * Async helper: collect fingerprint, get sessionId, build body for POST /v1/device-fingerprint.
 * Use with useSendDeviceFingerprintMutation: const body = await buildDeviceFingerprintBody('page_view'); send(body).
 */
export const buildDeviceFingerprintBody = async (
  eventType: DeviceFingerprintEventType,
  options?: { teamId?: number; merchantId?: string }
): Promise<DeviceFingerprintRequestBody> => {
  DeviceFingerprintService.clearCache();
  const fingerprint = await DeviceFingerprintService.collect();
  const sessionId = getOrCreateSessionId();
  return buildDeviceFingerprintRequestBody(fingerprint, {
    eventType,
    teamId: options?.teamId,
    merchantId: options?.merchantId,
    sessionId
  });
};

export const deviceFingerprintApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    sendDeviceFingerprint: builder.mutation<void, DeviceFingerprintRequestBody>({
      query: body => ({
        url: `${API_VERSION.V1}/device-fingerprint`,
        method: 'POST',
        body
      }),
      extraOptions: { showToast: false }
    })
  })
});

export const useSendDeviceFingerprintMutation = deviceFingerprintApi.useSendDeviceFingerprintMutation;
